import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Smile, Meh, Frown } from "lucide-react";

export default function Analytics() {
  const [f, setF] = useState(null);
  const [nps, setNps] = useState(null);
  useEffect(() => {
    api.get("/analytics/hiring-funnel").then(r => setF(r.data)).catch(() => {});
    api.get("/nps/summary/all").then(r => setNps(r.data)).catch(() => {});
  }, []);
  if (!f) return <div>Loading…</div>;

  const stages = Object.entries(f.by_stage);
  const max = Math.max(...stages.map(([_, v]) => v), 1);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Analytics</div>
        <h1 className="text-3xl font-bold font-display mt-1">Hiring analytics</h1>
        <p className="text-neutral-600 mt-1">Real-time conversion across pipeline stages plus candidate sentiment.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-6"><div className="text-xs uppercase text-neutral-500">Total Jobs</div><div className="text-4xl font-bold text-pink mt-2">{f.total_jobs}</div></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-6"><div className="text-xs uppercase text-neutral-500">Total Candidates</div><div className="text-4xl font-bold text-pink mt-2">{f.total_candidates}</div></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-6"><div className="text-xs uppercase text-neutral-500">Employees</div><div className="text-4xl font-bold text-teal mt-2">{f.total_employees}</div></CardContent></Card>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">Pipeline breakdown</h3>
          <div className="space-y-3">
            {stages.map(([k, v]) => (
              <div key={k}>
                <div className="flex items-center justify-between mb-1 text-sm">
                  <span className="font-medium">{k.replace(/_/g, " ")}</span>
                  <span className="text-neutral-500 font-mono">{v}</span>
                </div>
                <div className="h-3 rounded-full bg-neutral-100 overflow-hidden">
                  <div className="h-full bg-pink" style={{ width: `${(v / max) * 100}%`, transition: "width 400ms" }} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Candidate NPS Pulse */}
      {nps && (
        <Card className="border-0 shadow-sm" data-testid="analytics-nps-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-semibold flex items-center gap-2">Candidate NPS pulse <Badge variant="outline" className="text-[9px]">{nps.count || 0} responses</Badge></h3>
                <p className="text-xs text-neutral-500 mt-1">How candidates felt after their video interview.</p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase text-neutral-500">NPS</div>
                <div className={`text-4xl font-bold font-display ${nps.nps >= 50 ? "text-teal" : nps.nps >= 0 ? "text-pink" : "text-red-500"}`} data-testid="nps-score">{nps.nps ?? 0}</div>
              </div>
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl bg-teal/5 border border-teal/20 p-4 text-center" data-testid="nps-promoters">
                <Smile className="w-6 h-6 text-teal mx-auto" />
                <div className="text-2xl font-bold mt-2">{nps.promoters || 0}</div>
                <div className="text-xs text-neutral-500 mt-1">Promoters (great)</div>
              </div>
              <div className="rounded-xl bg-neutral-100 p-4 text-center" data-testid="nps-passives">
                <Meh className="w-6 h-6 text-neutral-500 mx-auto" />
                <div className="text-2xl font-bold mt-2">{nps.passives || 0}</div>
                <div className="text-xs text-neutral-500 mt-1">Passives (okay)</div>
              </div>
              <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-center" data-testid="nps-detractors">
                <Frown className="w-6 h-6 text-red-500 mx-auto" />
                <div className="text-2xl font-bold mt-2">{nps.detractors || 0}</div>
                <div className="text-xs text-neutral-500 mt-1">Detractors (rough)</div>
              </div>
            </div>
            {nps.recent?.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase text-neutral-500 mb-2">Recent responses</div>
                <div className="space-y-1.5">
                  {nps.recent.slice(0, 5).map((r, i) => {
                    const mood = r.response?.mood || (r.response?.score >= 9 ? "happy" : r.response?.score >= 7 ? "neutral" : "sad");
                    const Icon = mood === "happy" ? Smile : mood === "neutral" ? Meh : Frown;
                    return (
                      <div key={i} className="flex items-start gap-3 text-sm py-2 border-b last:border-b-0">
                        <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${mood==="happy"?"text-teal":mood==="sad"?"text-red-500":"text-neutral-500"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-neutral-700 truncate">{r.response?.comment || <span className="text-neutral-400 italic">no comment</span>}</div>
                          <div className="text-[11px] text-neutral-400 mt-0.5">{r.candidate_name || "Candidate"} · {new Date(r.response?.at || Date.now()).toLocaleDateString()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
